import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { directApiInstance } from '../api/httpClient';
// The wrapper, not baseui directly: BaseUI is frozen for new files (SAM-175, eslint.config.js).
import { toaster } from '../components/ui/toast';

/**
 * SAM-535 — which CyberSource account each business settles into.
 *
 * Talks to portalApi's action-style endpoint (`POST /portal` with an `action` in the body), the
 * same shape the payment calls already use.
 *
 * A config row NAMES a Secrets Manager secret; it never carries the credential. That is why there
 * is no field here for an API key, a shared secret or an organization id — the secret is created
 * out of band by whoever holds the CyberSource account, and the endpoint neither accepts nor
 * returns its contents. Do not add those fields.
 *
 * Authorization is the server's: the `xhinola_admin` group, re-checked on the token for every call.
 * useUserPermissions.isXhinolaAdmin decides what this app SHOWS; it is not the boundary.
 */

export interface MerchantConfig {
  id: string;
  tenant_id: string;
  /** null = the tenant-wide default, used by any business without one of its own. */
  business_id: string | null;
  business_name?: string | null;
  provider: string;
  /** Which SAM environment this row applies to. NOT which gateway it talks to — see gateway_mode. */
  environment: 'dev' | 'prod';
  secret_name: string;
  /**
   * Which CyberSource host this merchant reaches: 'test' (apitest) or 'live' (api).
   * null means unset, and the backend then falls back to the secret's own api_url and then to
   * PORTAL_API_URL.
   *
   * Independent of `environment` on purpose: that is what lets a payment run from SAM dev against
   * the live gateway to verify a real merchant profile.
   */
  gateway_mode: 'test' | 'live' | null;
  active: boolean;
  created_at?: string;
  updated_at?: string | null;
  created_by?: string | null;
  notes?: string | null;
}

export interface MerchantConfigInput {
  business_id?: string | null;
  environment: 'dev' | 'prod';
  secret_name: string;
  gateway_mode?: 'test' | 'live' | null;
  provider?: string;
  notes?: string | null;
}

const MERCHANT_CONFIG_KEY = ['merchant-config'];

/** httpClient's response interceptor already returns the BODY, not the axios response. */
const unwrap = <T,>(response: unknown): T => {
  const r = response as Record<string, unknown>;
  return (r?.data ?? r) as T;
};

/** The message the backend sent, when there is one worth showing. */
const messageFrom = (error: unknown, fallback: string): string => {
  const response = (error as { response?: { data?: { error?: string }; status?: number } })?.response;
  if (response?.status === 403) {
    // The backend's own message is deliberately generic; keep it that way here.
    return 'Your account cannot configure merchants.';
  }
  return response?.data?.error || fallback;
};

export const useMerchantConfigs = (enabled: boolean) => {
  return useQuery<MerchantConfig[], Error>({
    queryKey: MERCHANT_CONFIG_KEY,
    queryFn: async () => {
      const response = await directApiInstance({
        url: '/portal',
        method: 'POST',
        data: { action: 'list_merchant_config' },
      });
      return unwrap<{ configs: MerchantConfig[] }>(response).configs || [];
    },
    // Gated by the caller so a user without the group never fires a request that would 403.
    enabled,
    staleTime: 30000,
    retry: false,
  });
};

export const useUpsertMerchantConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: MerchantConfigInput) => {
      const response = await directApiInstance({
        url: '/portal',
        method: 'POST',
        data: {
          action: 'upsert_merchant_config',
          ...input,
          // An empty business means the tenant-wide default row, which the backend reads as NULL.
          business_id: input.business_id || null,
        },
      });
      return unwrap<{ config: MerchantConfig }>(response).config;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MERCHANT_CONFIG_KEY });
      toaster.positive('Merchant configuration saved', { autoHideDuration: 3000 });
    },
    onError: (error) => {
      toaster.negative(messageFrom(error, 'Could not save the merchant configuration'), {
        autoHideDuration: 5000,
      });
    },
  });
};

export const useDeactivateMerchantConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await directApiInstance({
        url: '/portal',
        method: 'POST',
        data: { action: 'deactivate_merchant_config', id },
      });
      return unwrap<{ config: MerchantConfig }>(response).config;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MERCHANT_CONFIG_KEY });
      toaster.info('Merchant configuration deactivated', { autoHideDuration: 4000 });
    },
    onError: (error) => {
      toaster.negative(messageFrom(error, 'Could not deactivate the merchant configuration'), {
        autoHideDuration: 5000,
      });
    },
  });
};
